import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  Popover,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useUserInformationContext } from 'contexts/userContext';
import { usePreferences } from 'contexts/preferences-context';
import { TFunction } from 'i18next';
import React, { FC, useEffect, useState } from 'react';
import { TITLE_COMPANY } from 'utils/constants';

const useStyles = makeStyles((theme: any) => ({
  popoverContainer: {
    position: 'relative',
    minWidth: '300px',
    width: '100%',
  },
  infoUserContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '15px 20px',
  },
  infoNameTitle: {
    fontSize: '23px',
    color: theme.palette.primary.light,
    marginTop: '17px',
  },
  infoMailTitle: {
    fontSize: '13px',
    color: theme.palette.primary.darker,
    fontWeight: 500,
  },
  divider: {
    height: 1,
    backgroundColor: theme.palette.button.default,
  },
  buttonLogOut: {
    padding: '10px 20px',
    width: '100%',
    fontSize: '14px',
    color: theme.palette.primary.darker,
    fontWeight: 500,
    justifyContent: 'flex-start',
    '& span, & > *': {
      textTransform: 'none !important',
      letterSpacing: 'normal !important',
    },
  },
  buttonLogOutIcon: {
    width: '16px !important',
    height: '16px !important',
    fontSize: '14px !important',
    color: theme.palette.primary.darker,
  },
  themeSection: {
    padding: '10px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  termsServiceTitle: {
    padding: '15px',
    width: '100%',
    fontSize: '11px',
    color: theme.palette.primary.light,
    textDecoration: 'underline',
    cursor: 'pointer',
    fontWeight: 500,
    textAlign: 'center',
  },
}));

type MenuProfileProps = {
  t: TFunction;
  anchorEl: HTMLElement | null;
  handleClose: () => void;
  open: boolean;
  handleNavigateProfile: () => void;
  handleNavigateTermsOfService: () => void;
  handleLogout: () => void;
  selectAgency: (agencyId: string) => void;
  agencySelected: string;
  handleOpenAiUsage: (email: string | null) => void;
};

export const MenuProfile: FC<MenuProfileProps> = ({
  open,
  anchorEl,
  handleClose,
  handleNavigateProfile,
  handleNavigateTermsOfService,
  handleLogout,
  selectAgency,
  agencySelected,
  t,
  handleOpenAiUsage,
}) => {
  const classes = useStyles();
  const { currentUser } = useUserInformationContext();
  const { selectedAgencyId, theme, setTheme } = usePreferences() as any;
  const [agencySelectorAnchorEl, setAgencySelectorAnchorEl] = useState<HTMLElement | null>(null);
  const titleCompanyParent = currentUser?.accesses.find(({ kind }) => kind === TITLE_COMPANY);
  const titleSubCompanies = currentUser?.accesses
    .filter(({ kind }) => kind !== TITLE_COMPANY)
    .sort((a, b) => a.name.localeCompare(b.name));

  // Close agency selector when popover closes
  if (!open && agencySelectorAnchorEl !== null) {
    setAgencySelectorAnchorEl(null);
  }

  const handleAgencySelectorOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAgencySelectorAnchorEl(event.currentTarget);
  };

  const handleAgencySelectorClose = () => {
    setAgencySelectorAnchorEl(null);
  };

  const handleAgencySelect = (agencyId: string) => {
    selectAgency(agencyId);
    handleAgencySelectorClose();
  };

  const getSelectedAgencyName = () => {
    if (!agencySelected) {
      return t('menu:all-agencies');
    }
    if (titleCompanyParent?._id === agencySelected) {
      return titleCompanyParent.name;
    }
    return titleSubCompanies?.find((a) => a._id === agencySelected)?.name || agencySelected;
  };

  useEffect(() => {
    if (open && selectedAgencyId !== agencySelected) {
      selectAgency(selectedAgencyId);
    }
  }, [open, selectedAgencyId, agencySelected, selectAgency]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <Box className={classes.popoverContainer}>
        <Box className={classes.infoUserContainer}>
          <Typography className={classes.infoNameTitle}>
            {currentUser?.name}
          </Typography>
          <Typography className={classes.infoMailTitle}>
            {currentUser && currentUser.emails.find((email) => email.kind === 'Login')?.address}
          </Typography>
        </Box>

        <Box>
          <Divider className={classes.divider} />
          <Button
            className={classes.buttonLogOut}
            startIcon={(
              <FontAwesomeIcon
                className={classes.buttonLogOutIcon}
                icon="home"
              />
             )}
            onClick={handleAgencySelectorOpen}
            aria-haspopup="true"
            aria-expanded={Boolean(agencySelectorAnchorEl)}
          >
            {getSelectedAgencyName()}
          </Button>
          <Button
            className={classes.buttonLogOut}
            startIcon={(
              <FontAwesomeIcon
                className={classes.buttonLogOutIcon}
                icon="user"
              />
             )}
            onClick={() => {
              handleClose();
              handleNavigateProfile();
            }}
          >
            {t('menu:profile')}
          </Button>
          {(() => {
            const loginEmail = currentUser?.emails.find((email) => email.kind === 'Login')?.address;
            return (
              <Button
                className={classes.buttonLogOut}
                disabled={!loginEmail}
                startIcon={(
                  <FontAwesomeIcon
                    className={classes.buttonLogOutIcon}
                    icon="chart-area"
                  />
                 )}
                onClick={() => {
                  if (loginEmail) {
                    handleClose();
                    handleOpenAiUsage(loginEmail);
                  } else {
                    handleClose();
                  }
                }}
              >
                {t('aiUsage:button')}
              </Button>
            );
          })()}

          <Menu
            anchorEl={agencySelectorAnchorEl}
            open={Boolean(agencySelectorAnchorEl)}
            onClose={handleAgencySelectorClose}
          >
            <MenuItem
              selected={agencySelected === ''}
              onClick={() => handleAgencySelect('')}
            >
              {t('menu:all-agencies')}
            </MenuItem>

            {titleCompanyParent && (
            <MenuItem
              selected={agencySelected === titleCompanyParent._id}
              onClick={() => handleAgencySelect(titleCompanyParent._id)}
            >
              {titleCompanyParent.name}
            </MenuItem>
            )}

            {titleSubCompanies && titleSubCompanies.map((agency) => (
              <MenuItem
                key={agency._id}
                selected={agencySelected === agency._id}
                onClick={() => handleAgencySelect(agency._id)}
              >
                {agency.name}
              </MenuItem>
            ))}
          </Menu>

          <Divider className={classes.divider} />
          <Box className={classes.themeSection}>
            <Typography variant="body2">{t('menu:theme', 'Theme')}</Typography>
            <Box display="flex" gap="8px">
              <Button
                size="small"
                variant={theme === 'light' ? 'contained' : 'outlined'}
                onClick={() => setTheme('light')}
              >
                {t('menu:theme-light', 'Light')}
              </Button>
              <Button
                size="small"
                variant={theme === 'dark' ? 'contained' : 'outlined'}
                onClick={() => setTheme('dark')}
              >
                {t('menu:theme-dark', 'Dark')}
              </Button>
            </Box>
          </Box>
          <Divider className={classes.divider} />
          <Button
            className={classes.buttonLogOut}
            startIcon={(
              <FontAwesomeIcon
                className={classes.buttonLogOutIcon}
                icon="sign-out-alt"
              />
            )}
            onClick={handleLogout}
          >
            {t('menu:log_out')}
          </Button>
          <Divider className={classes.divider} />
          <Typography
            className={classes.termsServiceTitle}
            onClick={() => {
              handleClose();
              handleNavigateTermsOfService();
            }}
          >
            {t('menu:terms_services')}
          </Typography>
        </Box>
      </Box>
    </Popover>
  );
};
